class VariantSelector {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.variantInput =
      container.closest('form')?.querySelector('input[name="id"]') ||
      document.getElementById(`product-form-${container.dataset.section || ''}`)?.querySelector('input[name="id"]') ||
      container.querySelector('[data-variant-input]');
    this.radioInputs = Array.from(container.querySelectorAll('input[type="radio"][data-level]'));
    this.currentSelections = {
      option1: null,
      option2: null,
      option3: null,
    };
    this.isSyncing = false;

    this.init();
  }

  init() {
    document.querySelector('.default-product-price')?.remove();
    this.setInitialSelections();
    this.radioInputs.forEach((input) => {
      input.addEventListener('change', (event) => this.handleOptionChange(event));
    });
    this.syncState();
  }

  setInitialSelections() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = parseInt(urlParams.get('variant'), 10);
    const variantData = this.getVariantData();
    const selectedVariant = variantId ? variantData.find((variant) => variant.id === variantId) : null;
    const checkedSelections = this.getCheckedSelections();
    const checkedVariant = this.findVariant(checkedSelections);
    const currentVariant = selectedVariant || checkedVariant || variantData.find((variant) => variant.available !== false) || variantData[0];

    if (!currentVariant) return;

    this.setSelectionsFromVariant(currentVariant);
  }

  handleOptionChange(event) {
    if (this.isSyncing) return;

    const input = event.target;
    const level = parseInt(input.dataset.level, 10);
    if (!level || input.disabled) return;

    this.currentSelections[`option${level}`] = input.value;
    this.completeSelections(level);
    this.syncState();
  }

  getCheckedSelections() {
    const selections = {};

    this.radioInputs.forEach((input) => {
      if (!input.checked || this.isInputHidden(input)) return;
      selections[`option${input.dataset.level}`] = input.value;
    });

    return selections;
  }

  isInputHidden(input) {
    const wrapper = input.closest('div[class*="drop-list-options-container-"]');
    return Boolean(wrapper && wrapper.classList.contains('wp-hidden'));
  }

  setSelectionsFromVariant(variant) {
    this.currentSelections.option1 = variant.option1 || null;
    this.currentSelections.option2 = variant.option2 || null;
    this.currentSelections.option3 = variant.option3 || null;
  }

  completeSelections(changedLevel) {
    for (let level = changedLevel + 1; level <= 3; level++) {
      const key = `option${level}`;
      if (!this.hasOptionLevel(level)) continue;
      if (this.currentSelections[key] && this.hasAvailableVariant(this.currentSelections, level)) continue;

      // option2 conventionally holds color variants — try to preserve the same color when other options change.
      const preferredValue = level === 2 ? this.findMatchingColorValue(level) : null;
      const nextValue = preferredValue || this.getFirstAvailableValue(level);
      this.currentSelections[key] = nextValue || null;
    }
  }

  hasOptionLevel(level) {
    return this.radioInputs.some((input) => parseInt(input.dataset.level, 10) === level);
  }

  hasAvailableVariant(selections, throughLevel = 3) {
    return this.getVariantData().some((variant) => {
      if (variant.available === false) return false;

      for (let level = 1; level <= throughLevel; level++) {
        const value = selections[`option${level}`];
        if (value && variant[`option${level}`] && variant[`option${level}`] !== value) return false;
      }

      return true;
    });
  }

  findMatchingColorValue(level) {
    const currentValue = this.currentSelections[`option${level}`];
    if (!currentValue) return null;

    const currentDisplayName = this.getColorDisplayName(currentValue);
    return this.getAvailableValues(level).find((value) => this.getColorDisplayName(value) === currentDisplayName) || null;
  }

  getFirstAvailableValue(level) {
    return this.getAvailableValues(level)[0] || null;
  }

  getAvailableValues(level) {
    const values = [];

    this.getVariantData().forEach((variant) => {
      if (variant.available === false || !variant[`option${level}`]) return;

      for (let previousLevel = 1; previousLevel < level; previousLevel++) {
        const value = this.currentSelections[`option${previousLevel}`];
        if (value && variant[`option${previousLevel}`] !== value) return;
      }

      values.push(variant[`option${level}`]);
    });

    return [...new Set(values)];
  }

  syncState() {
    const variant = this.findVariant(this.currentSelections) || this.findFirstAvailableVariant();
    if (!variant) return;

    this.setSelectionsFromVariant(variant);
    this.syncInputs();
    this.syncAvailability();
    this.syncDynamicPickerData();
    this.syncDynamicPickerVisibility(variant);
    this.updateProductPrice(variant);
    this.updateVariantInUrl(variant);
    this.updateGalleryByVariant(variant);
  }

  syncInputs() {
    this.isSyncing = true;

    this.radioInputs.forEach((input) => {
      const level = parseInt(input.dataset.level, 10);
      const checked = this.currentSelections[`option${level}`] === input.value;
      input.checked = checked;
      this.setAriaSelected(input, checked);
    });

    this.isSyncing = false;
  }

  syncAvailability() {
    this.radioInputs.forEach((input) => {
      const level = parseInt(input.dataset.level, 10);
      const selections = { ...this.currentSelections, [`option${level}`]: input.value };
      const available = this.hasAvailableVariant(selections, level);
      const optionDiv = input.closest('div[data-value]');

      input.disabled = !available;
      optionDiv?.classList.toggle('wp-hidden', !available);
    });
  }

  syncDynamicPickerData() {
    this.radioInputs.forEach((input) => {
      if (!input.classList.contains('drop-list-variant-picker-input')) return;

      const level = parseInt(input.dataset.level, 10);
      const variant = this.findVariant({ ...this.currentSelections, [`option${level}`]: input.value });
      if (!variant) return;

      const unit = input.dataset.unit || input.dataset.variantUnit || 'pc';
      input.dataset.variantId = variant.id;
      input.dataset.variantTitle = variant[`option${level}`] || '';
      input.dataset.variantPrice = this.formatMoney(variant.price);
      input.dataset.variantCompareAtPrice = this.hasCompareAtPrice(variant) ? this.formatMoney(variant.compare_at_price) : '';
      input.dataset.variantPriceUnit = `${this.formatMoney(this.getUnitPrice(variant.price, input.value))}/${unit}`;

      if (input.dataset.pickerMode === 'grid-card-price') {
        this.updateGridCardLabel(input, variant, level, unit);
      } else {
        this.updateDropListLabel(input, variant, unit);
      }
    });
  }

  updateGridCardLabel(input, variant, level, unit) {
    const priceElement = input.nextElementSibling?.querySelector('[data-grid-card-unit-price]');
    if (!priceElement) return;

    priceElement.textContent = `${this.formatMoney(this.getUnitPrice(variant.price, input.value))}/${unit}`;
    const titleElement = input.nextElementSibling?.querySelector('[data-grid-card-title]');
    if (titleElement) titleElement.textContent = variant[`option${level}`] || input.value;
  }

  updateDropListLabel(input, variant, unit) {
    const label = input.nextElementSibling;
    if (!label) return;

    const unitElement = label.querySelector('[data-variant-unit-price]');
    const priceElement = label.querySelector('[data-variant-price]');
    const compareElement = label.querySelector('[data-variant-compare-at-price]');

    if (unitElement) unitElement.textContent = `${this.formatMoney(this.getUnitPrice(variant.price, input.value))} ${this.getCurrency()} /${unit}`;
    if (priceElement) priceElement.textContent = `${this.formatMoney(variant.price)} ${this.getCurrency()}`;
    if (compareElement) compareElement.textContent = this.hasCompareAtPrice(variant) ? `${this.formatMoney(variant.compare_at_price)} ${this.getCurrency()}` : '';
  }

  syncDynamicPickerVisibility(variant) {
    const containers = this.container.querySelectorAll('div[class*="drop-list-options-container-"]');

    containers.forEach((container) => {
      const input = container.querySelector('input[data-level]');
      if (!input) return;

      const level = parseInt(input.dataset.level, 10);
      if (!Number.isInteger(level)) return;

      const expectedClass = `drop-list-options-container-${variant.id}-${level - 1}`;
      const shouldShow = container.classList.contains(expectedClass);
      container.classList.toggle('wp-hidden', !shouldShow);
    });
  }

  setAriaSelected(input, selected) {
    const li = input.closest('li[role="option"]');
    if (!li) return;

    li.setAttribute('aria-selected', selected ? 'true' : 'false');
  }

  findVariant(selections) {
    return this.getVariantData().find((variant) => {
      if (variant.available === false) return false;

      return [1, 2, 3].every((level) => {
        const value = selections[`option${level}`];
        return !value || !variant[`option${level}`] || variant[`option${level}`] === value;
      });
    });
  }

  findFirstAvailableVariant() {
    return this.getVariantData().find((variant) => variant.available !== false) || this.getVariantData()[0];
  }

  getVariantData() {
    if (window.tfxProduct && Array.isArray(window.tfxProduct.variants)) return window.tfxProduct.variants;

    return [];
  }

  updateProductPrice(variant) {
    const priceBlocks = document.querySelectorAll('.product-price');
    priceBlocks.forEach((block) => block.classList.add('wp-hidden'));

    const variantPrice = this.getCents(variant.price);
    const matchingPriceBlock = Array.from(priceBlocks).find((block) => {
      const blockPrice = parseInt(block.getAttribute('data-price'), 10);
      const hasComparePrice = block.querySelector('.wp-line-through') !== null;
      const variantHasComparePrice = this.hasCompareAtPrice(variant);

      return blockPrice === variantPrice && hasComparePrice === variantHasComparePrice;
    });

    matchingPriceBlock?.classList.remove('wp-hidden');
  }

  updateVariantInUrl(variant) {
    const scope = this.container.closest('form') || document;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const titleInput = scope.querySelector('input[name="title"]');
    if (titleInput) titleInput.value = variant.title;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateGalleryByVariant(variant) {
    const galleryItemId = variant?.featured_media?.id;
    const galleryItemPosition = variant?.featured_media?.position;
    if (!galleryItemId || !galleryItemPosition) return;

    const targetGalleryItem = document.querySelector(
      `.gallery-item[data-target-id="${galleryItemId}"][data-position="${galleryItemPosition}"]`
    );
    if (!targetGalleryItem) return;

    const gallery = targetGalleryItem.closest('.product_gallery');
    const index = Number(targetGalleryItem.dataset.index);
    if (gallery && Number.isInteger(index)) {
      gallery.dispatchEvent(new CustomEvent('product:select-media', { detail: { index } }));
    }
  }

  getColorDisplayName(value) {
    if (!value) return value;

    let name = value.split('|')[0].trim();
    const option1Set = new Set();
    this.getVariantData().forEach((variant) => {
      if (variant.option1) option1Set.add(variant.option1);
    });

    const prefixes = Array.from(option1Set)
      .flatMap((prefix) => [prefix, prefix.replace(/[- ]/g, '')])
      .sort((a, b) => b.length - a.length);

    for (const prefix of prefixes) {
      if (name.startsWith(`${prefix} `)) return name.slice(prefix.length + 1);
    }

    return name;
  }

  getUnitPrice(price, value) {
    // Pack-style option values start with a number (e.g. "2-pack"); non-numeric values fall through to the variant price.
    const quantity = parseInt(value, 10);
    const cents = this.getCents(price);

    if (quantity > 1) return Math.floor(cents / quantity);

    return cents;
  }

  hasCompareAtPrice(variant) {
    return this.getCents(variant.compare_at_price) > this.getCents(variant.price);
  }

  getCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return value;

    const normalized = String(value).replace(/[^0-9.]/g, '');
    if (!normalized) return 0;

    return normalized.includes('.') ? Math.round(parseFloat(normalized) * 100) : parseInt(normalized, 10);
  }

  formatMoney(value) {
    return (this.getCents(value) / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: this.getCurrency(),
    });
  }

  getCurrency() {
    return window.Shopify?.currency?.active || 'USD';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const variantSelectors = document.querySelectorAll('variant-selects');
  variantSelectors.forEach((container) => {
    new VariantSelector(container);
  });
});

if (typeof window !== 'undefined') {
  window.VariantSelector = VariantSelector;
}
